import {
  CollectionPreferencesProps,
  PaginationProps,
  TableProps,
} from '@amzn/awsui-components-react-v3';
import { useEffect, useState } from 'react';
import UserPreference from '../../services/preference';
import { SortOrder } from '../../types';

export type TableState<T> = {
  items: T[];
  setItems: (items: T[]) => void;
  pageCount: number;
  setPageCount: (pageCount: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortOrder: string;
  setSortOrder: (sortOrder: string) => void;
  totalCount: number;
  setTotalCount: (totalCount: number) => void;
  pageIndex: number;
  setPageIndex: (pageIndex: number) => void;
  visibleColumns: string[];
  setVisibleColumns: (columns: string[]) => void;
  onPaginationChange: (detail: PaginationProps.ChangeDetail) => void;
  onSortingChange: (detail: TableProps.SortingState<T>) => void;
  onPreferenceChange: (detail: CollectionPreferencesProps.Preferences) => void;
};

export type useTableProps = {
  tableId: string;
  pageSize: number;
  sortBy: string;
  sortOrder: string;
  visibleColumns: string[];
};

export type TablePreference = {
  pageSize: number;
  visibleColumns: string[];
  sortBy: string;
  sortOrder: string;
};

export default function useTable<T>(props: useTableProps): TableState<T> {
  const preferenceKey = `table-${props.tableId}`;
  const preferences = (UserPreference.get(preferenceKey) as TablePreference) || {};

  const [items, setItems] = useState<T[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(preferences.pageSize || props.pageSize);
  const [sortBy, setSortBy] = useState<string>(preferences.sortBy || props.sortBy);
  const [sortOrder, setSortOrder] = useState<string>(preferences.sortOrder || props.sortOrder);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    preferences.visibleColumns || props.visibleColumns
  );

  useEffect(() => {
    UserPreference.set(preferenceKey, { pageSize, visibleColumns, sortBy, sortOrder });
  }, [pageSize, visibleColumns, sortBy, sortOrder, preferenceKey]);

  const handlePaginationChange = (detail: PaginationProps.ChangeDetail) => {
    setPageIndex(detail.currentPageIndex);
  };

  const handleSortingChange = (detail: TableProps.SortingState<T>) => {
    setSortOrder(detail.isDescending ? SortOrder.DESC : SortOrder.ASC);
    setSortBy(detail.sortingColumn.sortingField as string);
  };

  const handlePreferenceChange = (detail: CollectionPreferencesProps.Preferences) => {
    setPageSize(detail.pageSize as number);
    setVisibleColumns(detail.visibleContent as string[]);
  };

  return {
    items,
    setItems,
    pageCount,
    setPageCount,
    pageSize,
    setPageSize,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    pageIndex,
    setPageIndex,
    totalCount,
    setTotalCount,
    visibleColumns,
    setVisibleColumns,
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onPreferenceChange: handlePreferenceChange,
  };
}
